/**
 * Color Palette Definitions
 * 
 * Each palette contains accent colors for both light and dark modes.
 * These palettes can be selected by tenants to personalize their dashboard.
 */

import { generateMutedColor, hexToRgba } from "@/lib/utils/color";

export type ColorPaletteId = "default" | "blue" | "green" | "purple" | "teal" | "pink" | "amber" | "indigo" | "red" | "cyan" | "emerald" | "coral" | "custom";

export interface ColorPalette {
  id: ColorPaletteId;
  name: string;
  description: string;
  // Primary accent color (for CTAs, active nav, hero emphasis)
  primary: string;
  // Muted accent color (for data viz, progress bars, secondary badges)
  muted: string;
  mutedHex: string;
  // Background colors for accents
  primaryBg: {
    dark: string;
    light: string;
  };
  primaryBorder: {
    dark: string;
    light: string;
  };
  mutedBg: {
    dark: string;
    light: string;
  };
  mutedBorder: {
    dark: string;
    light: string;
  };
  // Mesh gradient colors
  meshAccent: {
    dark: string;
    light: string;
  };
}

// COLOR_PALETTES doesn't include "custom" because it's generated dynamically
export const COLOR_PALETTES: Record<Exclude<ColorPaletteId, "custom">, ColorPalette> = {
  default: {
    id: "default",
    name: "be ClassManager",
    description: "Default BeClassManager brand accent. Our signature color for your studio.",
    primary: "#FF6347",
    muted: "rgba(255,99,71,0.65)",
    mutedHex: "#E05A42",
    primaryBg: {
      dark: "rgba(255,99,71,0.15)",
      light: "rgba(255,99,71,0.12)",
    },
    primaryBorder: {
      dark: "rgba(255,99,71,0.3)",
      light: "rgba(255,99,71,0.22)",
    },
    mutedBg: {
      dark: "rgba(255,99,71,0.1)",
      light: "rgba(255,99,71,0.14)",
    },
    mutedBorder: {
      dark: "rgba(255,99,71,0.2)",
      light: "rgba(255,99,71,0.18)",
    },
    meshAccent: {
      dark: "rgba(255,90,60,0.04)",
      light: "rgba(255,90,60,0.05)",
    },
  },
  blue: {
    id: "blue",
    name: "be Calm",
    description: "Calm and professional. Great for yoga and wellness studios.",
    primary: "#3965FF",
    muted: "rgba(57,101,255,0.65)",
    mutedHex: "#2D52CC",
    primaryBg: {
      dark: "rgba(57,101,255,0.15)",
      light: "rgba(57,101,255,0.12)",
    },
    primaryBorder: {
      dark: "rgba(57,101,255,0.3)",
      light: "rgba(57,101,255,0.22)",
    },
    mutedBg: {
      dark: "rgba(57,101,255,0.1)",
      light: "rgba(57,101,255,0.14)",
    },
    mutedBorder: {
      dark: "rgba(57,101,255,0.2)",
      light: "rgba(57,101,255,0.18)",
    },
    meshAccent: {
      dark: "rgba(57,101,255,0.04)",
      light: "rgba(57,101,255,0.05)",
    },
  },
  green: {
    id: "green",
    name: "be Fresh",
    description: "Fresh and natural. Ideal for growth and green-focused brands.",
    primary: "#22C55E",
    muted: "rgba(34,197,94,0.65)",
    mutedHex: "#16A34A",
    primaryBg: {
      dark: "rgba(34,197,94,0.15)",
      light: "rgba(34,197,94,0.12)",
    },
    primaryBorder: {
      dark: "rgba(34,197,94,0.3)",
      light: "rgba(34,197,94,0.22)",
    },
    mutedBg: {
      dark: "rgba(34,197,94,0.1)",
      light: "rgba(34,197,94,0.14)",
    },
    mutedBorder: {
      dark: "rgba(34,197,94,0.2)",
      light: "rgba(34,197,94,0.18)",
    },
    meshAccent: {
      dark: "rgba(34,197,94,0.04)",
      light: "rgba(34,197,94,0.05)",
    },
  },
  purple: {
    id: "purple",
    name: "be Creative",
    description: "Elegant and creative. Fits dance and arts studios.",
    primary: "#7551FF",
    muted: "rgba(117,81,255,0.65)",
    mutedHex: "#5D3FCC",
    primaryBg: {
      dark: "rgba(117,81,255,0.15)",
      light: "rgba(117,81,255,0.12)",
    },
    primaryBorder: {
      dark: "rgba(117,81,255,0.3)",
      light: "rgba(117,81,255,0.22)",
    },
    mutedBg: {
      dark: "rgba(117,81,255,0.1)",
      light: "rgba(117,81,255,0.14)",
    },
    mutedBorder: {
      dark: "rgba(117,81,255,0.2)",
      light: "rgba(117,81,255,0.18)",
    },
    meshAccent: {
      dark: "rgba(117,81,255,0.04)",
      light: "rgba(117,81,255,0.05)",
    },
  },
  teal: {
    id: "teal",
    name: "be Flow",
    description: "Modern and fluid. For movement and flow-based programs.",
    primary: "#14B8A6",
    muted: "rgba(20,184,166,0.65)",
    mutedHex: "#0D9488",
    primaryBg: {
      dark: "rgba(20,184,166,0.15)",
      light: "rgba(20,184,166,0.12)",
    },
    primaryBorder: {
      dark: "rgba(20,184,166,0.3)",
      light: "rgba(20,184,166,0.22)",
    },
    mutedBg: {
      dark: "rgba(20,184,166,0.1)",
      light: "rgba(20,184,166,0.14)",
    },
    mutedBorder: {
      dark: "rgba(20,184,166,0.2)",
      light: "rgba(20,184,166,0.18)",
    },
    meshAccent: {
      dark: "rgba(20,184,166,0.04)",
      light: "rgba(20,184,166,0.05)",
    },
  },
  pink: {
    id: "pink",
    name: "be Bold",
    description: "Soft and striking. For studios that want to stand out.",
    primary: "#EC4899",
    muted: "rgba(236,72,153,0.65)",
    mutedHex: "#DB2777",
    primaryBg: {
      dark: "rgba(236,72,153,0.15)",
      light: "rgba(236,72,153,0.12)",
    },
    primaryBorder: {
      dark: "rgba(236,72,153,0.3)",
      light: "rgba(236,72,153,0.22)",
    },
    mutedBg: {
      dark: "rgba(236,72,153,0.1)",
      light: "rgba(236,72,153,0.14)",
    },
    mutedBorder: {
      dark: "rgba(236,72,153,0.2)",
      light: "rgba(236,72,153,0.18)",
    },
    meshAccent: {
      dark: "rgba(236,72,153,0.04)",
      light: "rgba(236,72,153,0.05)",
    },
  },
  amber: {
    id: "amber",
    name: "be Warm",
    description: "Warm and energetic. Welcoming for all class types.",
    primary: "#FBBF24",
    muted: "rgba(251,191,36,0.65)",
    mutedHex: "#F59E0B",
    primaryBg: {
      dark: "rgba(251,191,36,0.15)",
      light: "rgba(251,191,36,0.12)",
    },
    primaryBorder: {
      dark: "rgba(251,191,36,0.3)",
      light: "rgba(251,191,36,0.22)",
    },
    mutedBg: {
      dark: "rgba(251,191,36,0.1)",
      light: "rgba(251,191,36,0.14)",
    },
    mutedBorder: {
      dark: "rgba(251,191,36,0.2)",
      light: "rgba(251,191,36,0.18)",
    },
    meshAccent: {
      dark: "rgba(251,191,36,0.04)",
      light: "rgba(251,191,36,0.05)",
    },
  },
  indigo: {
    id: "indigo",
    name: "be Pro",
    description: "Rich and professional. For serious academies and dojos.",
    primary: "#4338CA",
    muted: "rgba(67,56,202,0.65)",
    mutedHex: "#3730A3",
    primaryBg: {
      dark: "rgba(67,56,202,0.15)",
      light: "rgba(67,56,202,0.12)",
    },
    primaryBorder: {
      dark: "rgba(67,56,202,0.3)",
      light: "rgba(67,56,202,0.22)",
    },
    mutedBg: {
      dark: "rgba(67,56,202,0.1)",
      light: "rgba(67,56,202,0.14)",
    },
    mutedBorder: {
      dark: "rgba(67,56,202,0.2)",
      light: "rgba(67,56,202,0.18)",
    },
    meshAccent: {
      dark: "rgba(67,56,202,0.04)",
      light: "rgba(67,56,202,0.05)",
    },
  },
  red: {
    id: "red",
    name: "be Strong",
    description: "Bold and powerful. For martial arts and strength-focused brands.",
    primary: "#EF4444",
    muted: "rgba(239,68,68,0.65)",
    mutedHex: "#DC2626",
    primaryBg: {
      dark: "rgba(239,68,68,0.15)",
      light: "rgba(239,68,68,0.12)",
    },
    primaryBorder: {
      dark: "rgba(239,68,68,0.3)",
      light: "rgba(239,68,68,0.22)",
    },
    mutedBg: {
      dark: "rgba(239,68,68,0.1)",
      light: "rgba(239,68,68,0.14)",
    },
    mutedBorder: {
      dark: "rgba(239,68,68,0.2)",
      light: "rgba(239,68,68,0.18)",
    },
    meshAccent: {
      dark: "rgba(239,68,68,0.04)",
      light: "rgba(239,68,68,0.05)",
    },
  },
  cyan: {
    id: "cyan",
    name: "be Clear",
    description: "Bright and modern. Clean look for any business.",
    primary: "#06B6D4",
    muted: "rgba(6,182,212,0.65)",
    mutedHex: "#0891B2",
    primaryBg: {
      dark: "rgba(6,182,212,0.15)",
      light: "rgba(6,182,212,0.12)",
    },
    primaryBorder: {
      dark: "rgba(6,182,212,0.3)",
      light: "rgba(6,182,212,0.22)",
    },
    mutedBg: {
      dark: "rgba(6,182,212,0.1)",
      light: "rgba(6,182,212,0.14)",
    },
    mutedBorder: {
      dark: "rgba(6,182,212,0.2)",
      light: "rgba(6,182,212,0.18)",
    },
    meshAccent: {
      dark: "rgba(6,182,212,0.04)",
      light: "rgba(6,182,212,0.05)",
    },
  },
  emerald: {
    id: "emerald",
    name: "be Natural",
    description: "Rich and natural. Wellness and organic vibes.",
    primary: "#047857",
    muted: "rgba(4,120,87,0.65)",
    mutedHex: "#03543F",
    primaryBg: {
      dark: "rgba(4,120,87,0.15)",
      light: "rgba(4,120,87,0.12)",
    },
    primaryBorder: {
      dark: "rgba(4,120,87,0.3)",
      light: "rgba(4,120,87,0.22)",
    },
    mutedBg: {
      dark: "rgba(4,120,87,0.1)",
      light: "rgba(4,120,87,0.14)",
    },
    mutedBorder: {
      dark: "rgba(4,120,87,0.2)",
      light: "rgba(4,120,87,0.18)",
    },
    meshAccent: {
      dark: "rgba(4,120,87,0.04)",
      light: "rgba(4,120,87,0.05)",
    },
  },
  coral: {
    id: "coral",
    name: "be Inviting",
    description: "Soft and inviting. Friendly and approachable.",
    primary: "#F97316",
    muted: "rgba(249,115,22,0.65)",
    mutedHex: "#EA580C",
    primaryBg: {
      dark: "rgba(249,115,22,0.15)",
      light: "rgba(249,115,22,0.12)",
    },
    primaryBorder: {
      dark: "rgba(249,115,22,0.3)",
      light: "rgba(249,115,22,0.22)",
    },
    mutedBg: {
      dark: "rgba(249,115,22,0.1)",
      light: "rgba(249,115,22,0.14)",
    },
    mutedBorder: {
      dark: "rgba(249,115,22,0.2)",
      light: "rgba(249,115,22,0.18)",
    },
    meshAccent: {
      dark: "rgba(249,115,22,0.04)",
      light: "rgba(249,115,22,0.05)",
    },
  },
};

export const DEFAULT_PALETTE_ID: ColorPaletteId = "default";

const CUSTOM_PALETTE_STORAGE_KEY = "bcm:dashboard:custom-palette";

/**
 * Generate a complete color palette from a primary color
 */
export const generatePaletteFromColor = (primaryColor: string): Omit<ColorPalette, "id" | "name" | "description"> => {
  const mutedHex = generateMutedColor(primaryColor);

  return {
    primary: primaryColor,
    muted: hexToRgba(primaryColor, 0.65),
    mutedHex: mutedHex,
    primaryBg: {
      dark: hexToRgba(primaryColor, 0.15),
      light: hexToRgba(primaryColor, 0.12),
    },
    primaryBorder: {
      dark: hexToRgba(primaryColor, 0.3),
      light: hexToRgba(primaryColor, 0.22),
    },
    mutedBg: {
      dark: hexToRgba(primaryColor, 0.1),
      light: hexToRgba(primaryColor, 0.14),
    },
    mutedBorder: {
      dark: hexToRgba(primaryColor, 0.2),
      light: hexToRgba(primaryColor, 0.18),
    },
    meshAccent: {
      dark: hexToRgba(primaryColor, 0.04),
      light: hexToRgba(primaryColor, 0.05),
    },
  };
};

/**
 * Get custom palette from localStorage
 */
export const getCustomPalette = (): ColorPalette | null => {
  if (typeof window === "undefined") return null;
  
  try {
    const stored = localStorage.getItem(CUSTOM_PALETTE_STORAGE_KEY);
    if (!stored) return null;
    
    const data = JSON.parse(stored);
    if (!data.primary) return null;
    
    const paletteData = generatePaletteFromColor(data.primary);
    return {
      id: "custom",
      name: "be Unique",
      description: "Your own accent color. Pick any hex you like.",
      ...paletteData,
    };
  } catch {
    return null;
  }
};

/**
 * Save custom palette to localStorage
 */
export const saveCustomPalette = (primaryColor: string): void => {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem(CUSTOM_PALETTE_STORAGE_KEY, JSON.stringify({ primary: primaryColor }));
  } catch {
    // Ignore localStorage errors
  }
};

export const getColorPalette = (id: ColorPaletteId, customPrimaryColor?: string): ColorPalette => {
  if (id === "custom") {
    const primaryColor = customPrimaryColor || getCustomPalette()?.primary;
    if (primaryColor && primaryColor.startsWith("#")) {
      const paletteData = generatePaletteFromColor(primaryColor);
      return {
        id: "custom",
        name: "be Unique",
        description: "Your own accent color. Pick any hex you like.",
        ...paletteData,
      };
    }
    // Fallback to default if custom is invalid
    return COLOR_PALETTES[DEFAULT_PALETTE_ID];
  }
  
  return COLOR_PALETTES[id] || COLOR_PALETTES[DEFAULT_PALETTE_ID];
};

